import React, { useState, useEffect } from 'react';
import './BillingHistory.css';
import { useAuth } from '../../../Security/AuthProvider';

const BillingHistory = () => {
  const { getCredentials } = useAuth();
  const [periods, setPeriods] = useState([]);
  const [selectedPeriod, setSelectedPeriod] = useState(null);
  const [selectedData, setSelectedData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingPeriod, setLoadingPeriod] = useState(null);

  useEffect(() => {
    fetchPeriods();
  }, []);

  const fetchPeriods = async () => {
    try {
      setLoading(true);
      const credentials = getCredentials();
      
      const response = await fetch('/billing/periods', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          company_name: credentials.company_name,
          company_password: credentials.company_password,
        }),
      });

      const data = await response.json();
      
      if (data.success) {
        setPeriods(data.data.sort((a, b) => {
          if (a.year !== b.year) return b.year - a.year;
          return b.month - a.month;
        }));
      }
    } catch (error) {
      console.error('Erreur lors de la récupération des périodes:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPeriodData = async (year, month) => {
    try {
      setLoadingPeriod(`${year}-${month}`);
      const credentials = getCredentials();
      
      const response = await fetch(`/billing/month/${year}/${month}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          company_name: credentials.company_name,
          company_password: credentials.company_password,
        }),
      });

      const data = await response.json();
      
      if (data.success) {
        setSelectedData(data.data);
        setSelectedPeriod(`${year}-${String(month).padStart(2, '0')}`);
      }
    } catch (error) {
      console.error('Erreur lors de la récupération des données de la période:', error);
    } finally {
      setLoadingPeriod(null);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount);
  };

  const formatPeriod = (year, month) => {
    const monthNames = [
      'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
      'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'
    ];
    return `${monthNames[month - 1]} ${year}`;
  };

  if (loading) {
    return (
      <div className="billing-history">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Chargement de l'historique...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="billing-history">
      <div className="history-header">
        <h2>Historique de Facturation</h2>
        <p>Consultez les données de facturation des mois précédents</p>
      </div>

      <div className="history-content">
        <div className="periods-list">
          <h3>Périodes disponibles</h3>
          {periods.length === 0 ? (
            <div className="no-periods">
              <p>Aucune période de facturation trouvée.</p>
            </div>
          ) : (
            <div className="periods-grid">
              {periods.map((period) => (
                <div
                  key={period.period}
                  className={`period-card ${selectedPeriod === period.period ? 'active' : ''}`}
                  onClick={() => fetchPeriodData(period.year, period.month)}
                >
                  <div className="period-title">
                    {formatPeriod(period.year, period.month)}
                  </div>
                  <div className="period-subtitle">
                    {period.period}
                  </div>
                  {loadingPeriod === `${period.year}-${period.month}` && (
                    <div className="card-loading">Chargement...</div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {selectedData && (
          <div className="period-details">
            <h3>Détails - {formatPeriod(selectedData.month.split('-')[0], parseInt(selectedData.month.split('-')[1]))}</h3>
            
            <div className="summary-cards">
              <div className="summary-card">
                <div className="card-title">Total RDV</div>
                <div className="card-value">{selectedData.rdv_count}</div>
              </div>
              
              <div className="summary-card automatic">
                <div className="card-title">🤖 Automatiques</div>
                <div className="card-value">{selectedData.automatic_rdv_count}</div>
                <div className="card-percentage">
                  {selectedData.rdv_count > 0 ? 
                    Math.round((selectedData.automatic_rdv_count / selectedData.rdv_count) * 100) : 0}%
                </div>
              </div>
              
              <div className="summary-card semi-automatic">
                <div className="card-title">⚙️ Semi-automatiques</div>
                <div className="card-value">{selectedData.semi_automatic_rdv_count}</div>
                <div className="card-percentage">
                  {selectedData.rdv_count > 0 ? 
                    Math.round((selectedData.semi_automatic_rdv_count / selectedData.rdv_count) * 100) : 0}%
                </div>
              </div>
              
              <div className="summary-card manual">
                <div className="card-title">✋ Manuels</div>
                <div className="card-value">{selectedData.manual_rdv_count}</div>
                <div className="card-percentage">
                  {selectedData.rdv_count > 0 ? 
                    Math.round((selectedData.manual_rdv_count / selectedData.rdv_count) * 100) : 0}%
                </div>
              </div>
            </div>

            {selectedData.statistics && (
              <div className="statistics-section">
                <h4>Statistiques</h4>
                <div className="stats-grid">
                  <div className="stat-item">
                    <div className="stat-label">Revenus estimés</div>
                    <div className="stat-value revenue">
                      {formatCurrency(selectedData.statistics.total_revenue_estimate)}
                    </div>
                  </div>
                  
                  <div className="stat-item">
                    <div className="stat-label">Moyenne RDV/jour</div>
                    <div className="stat-value">
                      {selectedData.statistics.average_rdv_per_day}
                    </div>
                  </div>
                  
                  <div className="stat-item">
                    <div className="stat-label">Agent le plus actif</div>
                    <div className="stat-value">
                      {selectedData.statistics.most_active_agent || 'N/A'}
                    </div>
                  </div>
                  
                  <div className="stat-item">
                    <div className="stat-label">Opération la plus demandée</div>
                    <div className="stat-value">
                      {selectedData.statistics.most_requested_operation || 'N/A'}
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div className="rdv-summary">
              <h4>Résumé des rendez-vous ({selectedData.rdv_list.length})</h4>
              
              {/* Répartition par statut de facturation */}
              <div className="billing-status-summary">
                <div className="status-item pending">
                  <span>En attente:</span>
                  <strong>
                    {selectedData.rdv_list.filter(rdv => rdv.billing_status === 'pending').length}
                  </strong>
                </div>
                <div className="status-item billed">
                  <span>Facturé:</span>
                  <strong>
                    {selectedData.rdv_list.filter(rdv => rdv.billing_status === 'billed').length}
                  </strong>
                </div>
                <div className="status-item paid">
                  <span>Payé:</span>
                  <strong>
                    {selectedData.rdv_list.filter(rdv => rdv.billing_status === 'paid').length}
                  </strong>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BillingHistory; 